import { state } from '../state';
import { iconHash, iconSearch } from '../icons';

type JumpFn = (type: 'server' | 'channel' | 'dm', id: string) => void;

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

interface SwitcherItem {
  type: 'server' | 'channel' | 'dm';
  id: string;
  label: string;
  detail: string;
  icon: string;
}

function buildItems(): SwitcherItem[] {
  const items: SwitcherItem[] = [];

  // Servers
  for (const s of state.servers) {
    const initial = (s.name || '?').charAt(0).toUpperCase();
    items.push({
      type: 'server',
      id: s.id,
      label: s.name,
      detail: 'Server',
      icon: `<span class="qs-icon-circle">${esc(initial)}</span>`,
    });
  }

  // Channels for current server
  if (state.currentServer) {
    const chans = state.channels.filter(c => c.server_id === state.currentServer);
    for (const c of chans) {
      items.push({
        type: 'channel',
        id: c.name,
        label: `#${c.name}`,
        detail: state.servers.find(s => s.id === state.currentServer)?.name || '',
        icon: iconHash(),
      });
    }
  }

  // People (DMs)
  for (const p of state.people) {
    if (p.id === state.ownerId) continue;
    const initial = (p.name || '?').charAt(0).toUpperCase();
    items.push({
      type: 'dm',
      id: p.id,
      label: p.name,
      detail: p.email || 'DM',
      icon: `<span class="qs-icon-avatar">${esc(initial)}</span>`,
    });
  }

  return items;
}

export function openQuickSwitcher(onJump: JumpFn): void {
  // Remove any existing
  document.querySelector('.qs-overlay')?.remove();

  const allItems = buildItems();
  let filtered = allItems.slice(0, 15);
  let selectedIdx = 0;

  const overlay = document.createElement('div');
  overlay.className = 'qs-overlay';
  overlay.innerHTML = `
    <div class="qs-modal">
      <div class="qs-search-row">
        <span class="qs-search-icon">${iconSearch()}</span>
        <input type="text" class="qs-input" placeholder="Where would you like to go?" autocomplete="off" spellcheck="false">
      </div>
      <div class="qs-results" id="qs-results"></div>
      <div class="qs-footer">
        <span class="qs-hint"><kbd>&uarr;</kbd><kbd>&darr;</kbd> navigate</span>
        <span class="qs-hint"><kbd>Enter</kbd> select</span>
        <span class="qs-hint"><kbd>Esc</kbd> close</span>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const input = overlay.querySelector('.qs-input') as HTMLInputElement;
  const resultsEl = overlay.querySelector('#qs-results') as HTMLElement;

  function renderResults(): void {
    if (filtered.length === 0) {
      resultsEl.innerHTML = '<div class="qs-empty">No results found</div>';
      return;
    }
    resultsEl.innerHTML = filtered.map((item, i) => `
      <div class="qs-item ${i === selectedIdx ? 'qs-item-active' : ''}" data-idx="${i}">
        <span class="qs-item-icon">${item.icon}</span>
        <span class="qs-item-label">${esc(item.label)}</span>
        <span class="qs-item-detail">${esc(item.detail)}</span>
      </div>
    `).join('');

    // Click handler
    resultsEl.querySelectorAll<HTMLElement>('.qs-item').forEach(el => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const idx = parseInt(el.dataset['idx'] ?? '0');
        const item = filtered[idx];
        if (item) { close(); onJump(item.type, item.id); }
      });
      el.addEventListener('mouseenter', () => {
        const idx = parseInt(el.dataset['idx'] ?? '0');
        selectedIdx = idx;
        renderResults();
      });
    });
  }

  function close(): void {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  }

  input.addEventListener('input', () => {
    const q = input.value.trim().toLowerCase();
    if (!q) {
      filtered = allItems.slice(0, 15);
    } else {
      filtered = allItems.filter(item =>
        item.label.toLowerCase().includes(q) ||
        item.detail.toLowerCase().includes(q)
      ).slice(0, 15);
    }
    selectedIdx = 0;
    renderResults();
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedIdx = Math.min(selectedIdx + 1, filtered.length - 1);
      renderResults();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedIdx = Math.max(selectedIdx - 1, 0);
      renderResults();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[selectedIdx];
      if (item) { close(); onJump(item.type, item.id); }
      return;
    }
  };

  // Close on backdrop click
  overlay.addEventListener('mousedown', (e) => {
    if (e.target === overlay) close();
  });

  document.addEventListener('keydown', onKey);
  input.focus();
  renderResults();
}
